<template>
  <div v-if="$route.path.includes('book-now')">
    <main>
      <router-view />
    </main>
    <footer>
      <app-footer
        :communityName="$maxxpm.config.communityName"
        :streetAddress="$maxxpm.config.footer.address"
        :cityStateZip="$maxxpm.config.footer.cityStateZip"
        :phone="$maxxpm.config.footer.phone"
        :logoUrl="$maxxpm.config.footer.logo ?? $maxxpm.config.logo.src"
        :copyrightYear="$maxxpm.config.footer.copyrightYear"
      />
    </footer>
  </div>
  <div
    class="layout"
    v-else
  >
    <nav-bar
      :logoSrc="$maxxpm.config.logo.src"
      :logoAlt="$maxxpm.config.logo.alt"
      :bannerTitle="$maxxpm.config.nav.bannerTitle"
      :bannerSubtitle="$maxxpm.config.nav.bannerSubtitle"
      :bannerCTA="$maxxpm.config.nav.bannerCTA"
    />
    <div
      v-if="promoItems.length"
      id="incentive"
      class="promo-carousel"
    >
      <div class="promo-carousel-content">
        <p class="promo-message">{{ promoMessage }}</p>
        <Button
          label="View Availability"
          class="promo-cta"
          @click="router.push('/availability')"
        />
      </div>
      <div
        v-if="promoItems.length > 1"
        class="promo-dots"
      >
        <button
          v-for="(promo, index) in promoItems"
          :key="promo.id"
          type="button"
          class="promo-dot"
          :class="{ active: index === activePromoIndex }"
          :aria-label="`Promotion ${index + 1} of ${promoItems.length}`"
          @click="activePromoIndex = index"
        />
      </div>
      <p class="promo-disclaimer">*Offer subject to availability. Contact leasing for more details.</p>
    </div>
    <main>
      <router-view />
      <Dialog
        v-model:visible="showPopup"
        :modal="true"
        :dismissableMask="true"
        :style="{ width: '50vw' }"
        :breakpoints="{ '960px': '75vw', '641px': '95vw' }"
      >
        <template #header>
          <h2 class="text-center flex-grow-1">Don't wait, schedule to visit the property today!</h2>
        </template>
        <div class="flex justify-content-center p-4 popup-showing">
          <Button
            @click="openShowingPopup"
            aria-haspopup="dialog"
            label="Schedule a Showing"
          ></Button>
        </div>
      </Dialog>
    </main>
    <footer>
      <app-footer
        :communityName="$maxxpm.config.communityName"
        :streetAddress="$maxxpm.config.footer.address"
        :cityStateZip="$maxxpm.config.footer.cityStateZip"
        :phone="$maxxpm.config.footer.phone"
        :logoUrl="$maxxpm.config.footer.logo ?? $maxxpm.config.logo.src"
        :copyrightYear="$maxxpm.config.footer.copyrightYear"
      />
    </footer>
  </div>
</template>

<script setup>
import { inject, ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import bookingMessageHandler from "./utils/booking_message_handler";

const $maxxpm = inject("$maxxpm");
const router = useRouter();

const showPopup = ref(false);
const Calendly = ref(null);
const QRG = ref(null);
const promoItems = ref([]);
const activePromoIndex = ref(0);
const isMobile = ref(false);
let promoRotateTimer = null;
let mobileMediaQuery = null;

const promoMessage = computed(() => {
  const promo = promoItems.value[activePromoIndex.value];
  if (!promo) return "";
  return isMobile.value ? promo.badge : promo.description;
});

function dedupePromotions(listings) {
  const seen = new Set();
  const promos = [];
  for (const listing of listings) {
    for (const promotion of listing.promotions ?? []) {
      if (!seen.has(promotion.id)) {
        seen.add(promotion.id);
        promos.push(promotion);
      }
    }
  }
  return promos;
}

function updateMobile() {
  isMobile.value = mobileMediaQuery?.matches ?? window.innerWidth <= 768;
}

function startPromoRotation() {
  if (promoRotateTimer) clearInterval(promoRotateTimer);
  if (promoItems.value.length <= 1) return;
  promoRotateTimer = setInterval(() => {
    activePromoIndex.value = (activePromoIndex.value + 1) % promoItems.value.length;
  }, 5000);
}

async function fetchPromotions() {
  const apiUrl = process.env.VUE_APP_LISTINGS_API_URL;
  if (!apiUrl) return;

  try {
    const { data } = await axios.get(`${apiUrl}/listings?property_group=${$maxxpm.config.availability.propertyGroup}`);
    promoItems.value = dedupePromotions(data);
    activePromoIndex.value = 0;
    startPromoRotation();
  } catch {
    promoItems.value = [];
  }
}

const getListingAvailableForShowing = async () => {
  const listings = await axios.get(`${process.env.VUE_APP_LISTINGS_API_URL}/listings?property_group=${$maxxpm.config.availability.propertyGroup}`);
  if (listings.data.length) {
    const availableForShowing = listings.data.find((listing) => {
      if (listing.microsoftBookingServiceId || listing.community.microsoftBookingServiceId || listing.showingUrl || listing.community.showingUrl) {
        if (listing.readyForShowingOn) {
          return new Date(listing.readyForShowingOn) < new Date();
        } else if (listing.activeUnit && listing.activeUnit.readyForShowingOn) {
          return new Date(listing.activeUnit.readyForShowingOn) < new Date();
        } else if (listing.activeUnit && listing.activeUnit.availableOn) {
          const availableOn = new Date(listing.activeUnit.availableOn);
          const sevenDaysBeforeAvailableOn = new Date(availableOn.getTime());
          sevenDaysBeforeAvailableOn.setUTCDate(availableOn.getUTCDate() - 7);
          return sevenDaysBeforeAvailableOn <= new Date();
        }
      }
      return false;
    });
    return availableForShowing;
  }
  return null;
};

let urlParams;
const availableForShowing = ref(null);

async function openShowingPopup() {
  if (availableForShowing.value) {
    if (availableForShowing.value.microsoftBookingServiceId || availableForShowing.value.community.microsoftBookingServiceId) {
      QRG.value.open({ communityId: availableForShowing.value.community.id, listingId: availableForShowing.value.id });
    } else if (availableForShowing.value.showingUrl) {
      Calendly.value.initPopupWidget({
        url: availableForShowing.value.showingUrl,
        utm: {
          utmSource: urlParams.get("utm_source") || "entratafarmstownhomes.com",
          utmMedium: "listing",
          utmContent: availableForShowing.value.id,
        },
      });
    } else {
      Calendly.value.initPopupWidget({
        url: availableForShowing.value.community.showingUrl,
        utm: {
          utmSource: urlParams.get("utm_source") || "entratafarmstownhomes.com",
          utmMedium: "listing",
          utmContent: availableForShowing.value.id,
        },
      });
    }
  }
}

onMounted(async () => {
  mobileMediaQuery = window.matchMedia("(max-width: 768px)");
  updateMobile();
  mobileMediaQuery.addEventListener("change", updateMobile);
  fetchPromotions();
  availableForShowing.value = await getListingAvailableForShowing();
  window.addEventListener("message", bookingMessageHandler);

  Calendly.value = window.Calendly;
  QRG.value = window.QrgBooking;
  urlParams = new URLSearchParams(window.location.search);
  const cname = "showPopup";
  setTimeout(() => {
    if ($maxxpm.config.hasShowingPopup && availableForShowing.value && !document.cookie.split("; ").find((c) => c.startsWith(cname))) {
      document.cookie = `${cname}=true; max-age=${60 * 60 * 24 * 1}`;
      showPopup.value = true;
    }
  }, 3500);
});

onUnmounted(() => {
  if (promoRotateTimer) clearInterval(promoRotateTimer);
  mobileMediaQuery?.removeEventListener("change", updateMobile);
  window.removeEventListener("message", bookingMessageHandler);
});
</script>

<style>
#app {
  font-family: Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

.layout {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100vh;
  position: relative;
}

body {
  margin: 0;
}

.img-fluid {
  max-width: 100%;
  height: auto;
}

.promo-carousel {
  background: var(--primary-color);
  color: #fff;
  padding: 0.45rem 1rem 0.3rem;
}

.promo-carousel-content {
  max-width: min(1200px, 95vw);
  margin: 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.85rem;
  flex-wrap: wrap;
}

.promo-message {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  text-align: center;
}

.promo-cta.p-button {
  background: #fff !important;
  color: var(--primary-color) !important;
  border-color: #fff !important;
  font-weight: 700;
  padding: 0.35rem 0.85rem;
}

.promo-dots {
  display: flex;
  justify-content: center;
  gap: 0.4rem;
  margin-top: 0.35rem;
}

.promo-dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.45);
  cursor: pointer;
}

.promo-dot.active {
  background: #fff;
}

.promo-disclaimer {
  margin: 0.25rem 0 0;
  text-align: center;
  font-size: 0.9rem;
  opacity: 0.95;
}

@media (max-width: 768px) {
  .promo-carousel-content {
    flex-direction: column;
  }

  .promo-disclaimer {
    font-size: 0.8rem;
  }
}
</style>
